import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  FiTrendingUp, FiDollarSign, FiClock, FiAlertCircle, FiUsers, FiMonitor,
  FiFileText, FiActivity,
} from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { formatMoney } from '../../utils/format';
import { showError } from '../../utils/alert';
import StatusBadge from '../../components/common/StatusBadge';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import { COMPANY_NAME } from '../../config';

const PIE_COLORS = ['#00a76f', '#00b8d9', '#ffab00', '#ff5630', '#8b5cf6', '#22c55e'];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.dashboard.summary();
      setData(res);
    } catch (err) {
      showError('Failed to load dashboard', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader text="Loading dashboard..." />;
  if (!data) return null;

  const s = data.summary || {};
  const trend = (data.sales_trend || []).map((t) => ({ name: t.label, sales: Number(t.total || 0) }));
  const pieData = (data.invoice_status_breakdown || []).map((p) => ({
    name: p.status,
    value: Number(p.count || 0),
  }));

  const stats = [
    { icon: <FiTrendingUp />, color: 'indigo', label: "Today's Sales", value: formatMoney(s.total_sales_today) },
    { icon: <FiDollarSign />, color: 'green', label: 'Month Sales', value: formatMoney(s.total_sales_month) },
    { icon: <FiClock />, color: 'orange', label: 'Receivables', value: formatMoney(s.total_receivables) },
    { icon: <FiAlertCircle />, color: 'red', label: 'Payables', value: formatMoney(s.total_payables) },
    { icon: <FiDollarSign />, color: 'blue', label: 'Cash & Bank', value: formatMoney(s.cash_bank_balance) },
    { icon: <FiFileText />, color: 'purple', label: 'Pending Invoices', value: s.pending_invoices },
    { icon: <FiUsers />, color: 'cyan', label: 'Active Employees', value: s.active_employees },
    { icon: <FiMonitor />, color: 'green', label: 'Active Machines', value: s.active_machines },
  ];

  return (
    <div className="page">
      <PageHeader
        title={`Good ${new Date().getHours() < 12 ? 'Morning' : 'Afternoon'}, Admin`}
        subtitle={`Welcome back to ${COMPANY_NAME} ERP. Here's what's happening today.`}
      />

      <div className="stats-grid">
        {stats.map((st) => (
          <div key={st.label} className="stat-card">
            <div className={`stat-icon ${st.color}`}>{st.icon}</div>
            <div>
              <div className="stat-value">{st.value}</div>
              <div className="stat-label">{st.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-grid">
        <div className="card chart-box">
          <div className="card-title">Sales Trend — Last 6 Months</div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00a76f" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#00a76f" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis dataKey="name" fontSize={12} tick={{ fill: '#7c8698' }} axisLine={false} tickLine={false} />
              <YAxis fontSize={12} tick={{ fill: '#7c8698' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Area type="monotone" dataKey="sales" stroke="#00a76f" strokeWidth={3} fill="url(#salesGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card chart-box">
          <div className="card-title">Invoice Status</div>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><h4>No invoices yet</h4></div>
          )}
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: 22 }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Invoices</div>
            <Link to="/invoices" className="btn btn-secondary btn-sm">View all</Link>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Party</th>
                  <th>Status</th>
                  <th className="table-num">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_invoices || []).map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.invoice_no}</td>
                    <td>{inv.party ? inv.party.name : '-'}</td>
                    <td><StatusBadge status={inv.status} /></td>
                    <td className="table-num">{formatMoney(inv.total)}</td>
                  </tr>
                ))}
                {(!data.recent_invoices || data.recent_invoices.length === 0) && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No recent invoices</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title"><FiActivity style={{ verticalAlign: 'middle', marginRight: 6 }} />Recent Activity</div>
            <Link to="/activity-logs" className="btn btn-secondary btn-sm">View all</Link>
          </div>
          <div className="card-body" style={{ padding: '8px 20px' }}>
            {(data.recent_activity || []).map((log) => (
              <div key={log.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 13, borderRadius: 8 }}>
                  {log.user ? log.user.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
                </div>
                <div>
                  <div style={{ fontSize: 13 }}>
                    <b>{log.user ? log.user.name : 'System'}</b> <StatusBadge status={log.activity} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{log.description}</div>
                </div>
              </div>
            ))}
            {(!data.recent_activity || data.recent_activity.length === 0) && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30, fontSize: 13 }}>No recent activity</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}